import React from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Button from './Button';
import IconButton from './IconButton';
import DLCStatus from 'constants/10-dlc-status';
import { hide10DLCBanner } from 'store/modules/modals/actions';
import { updateNotification } from 'store/modules/notifications/actions';
import useSenderProfileStatus from '../hooks/useSenderProfileStatus';

export const UnregisteredBanner = () => {
  const [t] = useTranslation('senderProfile');
  return (
    <Stack
      direction="row"
      spacing={3}
      justifyContent="center"
      alignItems="center"
      sx={{ bgcolor: 'error.main', color: 'white', p: 1.5 }}
    >
      <Box textAlign="center">
        <Typography fontWeight={600} color="inherit">
          {t(
            'unregistered-banner-title',
            'You have not completed your A2P 10-DLC registration',
          )}
        </Typography>
        <Typography color="inherit">
          {t(
            'unregistered-banner-description',
            'Registration is Required. Don’t let your messages be flagged as SPAM  or blocked by cell phone carriers.',
          )}
        </Typography>
      </Box>
      <Button
        component={Link}
        to="/settings/sender-profile"
        color="inverse"
        sx={{ color: 'error.main' }}
      >
        {t('unregistered-banner-cta', 'Register My Account')}
      </Button>
    </Stack>
  );
};

const FailedBanner = () => {
  const openIntercom = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  return (
    <Stack
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={{
        bgcolor: 'error.main',
        color: 'white',
        p: 1.5,
        textAlign: 'center',
      }}
    >
      <Typography variant="h3" color="inherit" fontWeight={500}>
        Your account is <strong>Failed</strong> registration for{' '}
        <span style={{ textDecoration: 'underline' }}>A2P 10-DLC</span>
      </Typography>
      <Typography color="inherit">
        A2P 10DLC is the registration system that allows organizations to send
        Application-to-Person (A2P) type messaging via standard 10-digit long
        code (10DLC) phone numbers.
      </Typography>
      <Typography fontWeight={600} color="inherit">
        Our team has been notified to fix this issue but we’re here to talk
        about if you have questions.
      </Typography>
      <Button onClick={openIntercom} color="inverse">
        Contact Support
      </Button>
    </Stack>
  );
};

export const SuccessBanner = ({ onClose }) => {
  const [t] = useTranslation('senderProfile');
  return (
    <Box bgcolor="success.main" textAlign="center" position="relative" p={1.5}>
      <Typography fontWeight={600} color="white">
        {t(
          'success-banner-title',
          'Yippee! Your A2P 10-DLC Registration Has Been Approved. Enjoy faster sending speeds and better deliverability. Happy sending!',
        )}
      </Typography>
      <Typography color="white">
        {t(
          'success-banner-desc',
          'Enjoy fast sending speeds and great deliverability. Happy sending!',
        )}
      </Typography>
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', right: 10, top: 8, color: 'white' }}
        icon={<CloseIcon />}
      />
    </Box>
  );
};

export const PendingBanner = ({ onClose, isTempMsgService }) => {
  const [t] = useTranslation('senderProfile');

  if (isTempMsgService) {
    return (
      <Box
        bgcolor="warning.main"
        textAlign="center"
        position="relative"
        p={1.5}
      >
        <Typography fontWeight={600}>
          {t(
            'pending-tmp-svg-banner-title',
            'Registration pending carrier approval.',
          )}
        </Typography>
        <Typography>
          {t(
            'pending-tmp-svg-banner-desc',
            'Your messages will be manually vetted which may cause minor delays (typically 7-10 business days).',
          )}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 10, top: 8 }}
          icon={<CloseIcon />}
        />
      </Box>
    );
  }

  return (
    <Box bgcolor="warning.main" textAlign="center" position="relative" p={1.5}>
      <Typography fontWeight={600}>
        {t(
          'pending-banner-title',
          'Good News! Your A2P 10-DLC registration is pending approval. Nothing else is needed at this time.',
        )}
      </Typography>
      <Typography>
        {t(
          'pending-banner-desc',
          'Your messaging will be available once approved. (typically 7-10 business days)',
        )}
      </Typography>
    </Box>
  );
};

const SenderProfileStatus = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const showBanner = useSelector((s) => s.modals.show10DLCBanner);
  const connections = useSelector((s) => s.settings.account.connections);
  const { status } = useSenderProfileStatus();
  const isSamePage = location.pathname === '/settings/sender-profile';
  const isTempMsgService = connections?.find(
    (c) => c.is_temp_msg_service === '1',
  );

  const onClose = () => {
    dispatch(
      updateNotification({
        hash: showBanner.hash,
        notifications_seen: '1',
      }),
    );
    dispatch(hide10DLCBanner());
  };

  if (isSamePage) return null;
  // if (status === DLCStatus.FAILED) {
  //   return <FailedBanner />;
  // }
  if (
    showBanner &&
    [DLCStatus.PENDING, DLCStatus.IN_PROGRESS].includes(status)
  ) {
    return (
      <PendingBanner onClose={onClose} isTempMsgService={isTempMsgService} />
    );
  }
  if (showBanner && status === DLCStatus.COMPLETED) {
    return <SuccessBanner onClose={onClose} />;
  }
  if (status === DLCStatus.NEED_INFO) {
    return <UnregisteredBanner />;
  }
  return null;
};

export default SenderProfileStatus;
